import {
  CalculatorOutlined,
  ExportOutlined,
  FieldTimeOutlined,
  FlagOutlined,
  GiftOutlined,
  MedicineBoxOutlined,
  SunOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { Form } from 'antd';
import {
  FieldCard,
  Group,
  SettingChoice,
  SettingSwitch,
} from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';
import { WorkTypesSelect } from './CompanyPayrollFields';

/**
 * The "Salary calculation" tile of the company profile as setting cards. Same
 * fields, visibility rules and save as CompanyPayrollFields, which steps aside
 * on the profile.
 */
export default function CompanyPayrollProfilePanel() {
  const ctx = useCompanyGeneralDataForm();
  const {
    enumsDataIsLoading,
    hasSalaryCalculationType,
    readOnly,
    salaryCalculationOptions,
    sectionClass,
    t,
    workTypesKeys,
    workTypesOptions,
    workTypesState,
  } = ctx;
  const f = (key) => t(key, { ns: 'forms' });
  const ui = (key) =>
    t(`companyProfile.cardsUi.${key}`, { ns: 'translations' });
  const lock = (state) => readOnly || !!state?.disabled;
  const visible = (name) => !!ctx[`${name}State`]?.visible;

  // `name` = the context prefix of <name>Keys / <name>State.
  const choice = (name, props) =>
    visible(name) && (
      <Form.Item name={ctx[`${name}Keys`]} noStyle>
        <SettingChoice
          locked={lock(ctx[`${name}State`])}
          options={salaryCalculationOptions}
          {...props}
        />
      </Form.Item>
    );
  const toggle = (name, props) =>
    visible(name) && (
      <Form.Item name={ctx[`${name}Keys`]} noStyle>
        <SettingSwitch locked={lock(ctx[`${name}State`])} {...props} />
      </Form.Item>
    );

  return (
    <div className={cn('grid gap-4', sectionClass('obracun'))}>
      {visible('salaryCalculationType') && (
        <Group title={ui('groupCalcMethod')}>
          {choice('salaryCalculationType', {
            icon: <CalculatorOutlined />,
            title: t('fixedAverageCalculation'),
          })}
        </Group>
      )}

      {hasSalaryCalculationType && (
        <Group title={ui('groupCalcPerLeave')}>
          {choice('holidayLeaveConfirmation', {
            icon: <SunOutlined />,
            title: f('holidayLeaveConfirmation'),
          })}
          {choice('sickLeaveUpTo30Days65Confirmation', {
            icon: <MedicineBoxOutlined />,
            title: f('sickLeaveUpTo30Days65Confirmation'),
          })}
          {choice('sickLeaveUpTo30Days100Confirmation', {
            icon: <MedicineBoxOutlined />,
            title: f('sickLeaveUpTo30Days100Confirmation'),
          })}
          {choice('paidLeaveConfirmation', {
            icon: <GiftOutlined />,
            title: f('paidLeaveConfirmation'),
          })}
          {choice('publicHolidayConfirmation', {
            icon: <FlagOutlined />,
            title: f('publicHolidayConfirmation'),
          })}
        </Group>
      )}

      <Group title={ui('groupWorkTypes')}>
        {workTypesState?.visible && (
          <FieldCard icon={<TagsOutlined />} title={f('typesOfWork')}>
            <Form.Item name={workTypesKeys} className="!mb-0">
              <WorkTypesSelect
                options={workTypesOptions}
                loading={enumsDataIsLoading}
                disabled={lock(workTypesState)}
              />
            </Form.Item>
          </FieldCard>
        )}
        {toggle('overTime2', {
          icon: <FieldTimeOutlined />,
          title: f('overtime2'),
        })}
        {toggle('extendedAbExport', {
          icon: <ExportOutlined />,
          title: t('extendedAbExport', {
            ns: 'forms',
            defaultValue: 'Extended AB export (columns 50–60)',
          }),
        })}
      </Group>
    </div>
  );
}
